import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";

import {
  Loader,
  NotificationType,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@transfr-inc/dashboard-components";

import { Button } from "@transfr-inc/dashboard-components/forms";

import { TraineesEditor } from "@views/classrooms/creation/trainees-editor";
import { UserOptionItem } from "@views/classrooms/creation/user-option-item";

import { catchUserCreationError } from "@services";
import { RecordStatus } from "@components/custom/dropdowns";
import { Role } from "@models";
import container from "@src/container";

import { ConfirmCancelModal } from "./confirm-cancel.modal";

import "./add-user.modal.scss";
import { useQueryClient } from "@tanstack/react-query";
import { useNotificationContext } from "@contexts/notification-context";
import { useApiRequest } from "@lib/hooks";
import { useContainer } from "@src/contexts/container-context";
import { useCurrentUser, useRole } from "@queries";

import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

export function AddUserModal({
  open,
  orgCode,
  onAddTrainee,
  async,
  classroomId,
  onCancel,
  classroomProduct,
  allProducts,
  openRosterModal,
  isProfessionalLearning = false,
  userLabels = {
    singular: `${getLearnerTerm({ lowercase: true })}`,
    plural: `${getLearnerTerm({ lowercase: true, plural: true })}`,
  },
}) {
  const { singular, plural } = userLabels;
  const [trainees, setTrainees] = useState([]);
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false);
  const [traineesCreated, setTraineesCreated] = useState(false);
  const [selectedTrainees, setSelectedTrainees] = useState([]);
  const [loading, setLoading] = useState();
  const { addNotification } = useNotificationContext();
  const { userService, classroomService } = useContainer();
  const queryClient = useQueryClient();

  const role = useRole();
  const currentUser = useCurrentUser();
  const { userId, organizationCode } = currentUser || {};

  const { sendRequest: getUsers } = useApiRequest(() => {
    queryClient.invalidateQueries({
      queryKey: ["organizationUsers", role, organizationCode, userId],
    });
    return Promise.resolve();
  }, false);

  const methods = useForm({
    criteriaMode: "all",
    mode: "onChange",
    defaultValues: {
      name: "",
      organization: { code: orgCode },
    },
  });

  // Watch for changes in the select user component
  const traineesWatch = methods.watch("trainees");

  useEffect(() => {
    traineesWatch && setSelectedTrainees([...traineesWatch]);
  }, [traineesWatch]);

  const onConfirmCloseHandler = () => {
    setOpenConfirmCancel(false);
    setTraineesCreated(false);
    onCloseHandler(true);
  };

  const onBackToModalHandler = () => {
    setOpenConfirmCancel(false);
    traineesCreated && onCancel(true);
  };

  const onCloseHandler = (confirmed) => {
    if (traineesCreated && !confirmed) {
      onCancel && onCancel();
      setOpenConfirmCancel(true);
    } else {
      onCancel && onCancel();
      methods.reset();
    }
  };

  const onAddTraineeHandler = () => {
    setTraineesCreated(false);
    if (async) {
      asyncHandler(selectedTrainees);
    } else {
      onAddTrainee(selectedTrainees);
      getUsers();
    }
  };

  const asyncHandler = (value) => {
    setLoading(true);
    onAddTrainee(value)
      .catch(() =>
        addNotification({
          type: NotificationType.error,
          message: "Something went wrong. Please try again.",
        })
      )
      .finally(() => {
        setLoading(false);
        getUsers();
      });
  };

  const addTraineesToForm = (...trainees) => {
    const updatedTrainees = [...selectedTrainees, ...trainees];
    methods.setValue("trainees", updatedTrainees);
  };

  const onCreateTrainee = async (user) => {
    try {
      return await createUser(user);
    } catch (error) {
      console.log(error);
      return catchUserCreationError(error);
    }
  };

  const createUser = async (user) => {
    const student = await userService.createNewUser(user, orgCode, allProducts);
    await fetchStudentUsers();
    const studentOption = new UserOptionItem(student);
    addTraineesToForm(studentOption);
    setTraineesCreated(true);
    return student;
  };

  const onAddTrainees = async ({ trainees, success, error }) => {
    if (trainees?.length > 0) {
      setTraineesCreated(true);
      await fetchStudentUsers();
      const optionItems = trainees.map((user) => new UserOptionItem(user));
      addTraineesToForm(...optionItems);
    }

    if (success) {
      addNotification(success);
    }

    if (error) {
      addNotification(error);
    }
  };

  async function fetchStudentUsers() {
    const availableUsers = isProfessionalLearning
      ? await userService.getOrganizationUsersForRoles(orgCode, [
          Role.INSTRUCTOR.id,
          Role.ORG_ADMIN.id,
        ])
      : await userService.getOrganizationUsersForRole(
          orgCode,
          Role.STUDENT.id,
          RecordStatus.Active
        );
    const alreadyEnrolled = await classroomService.getClassroomTrainees(
      classroomId
    );
    const alreadyEnrolledIds = new Set(alreadyEnrolled.map((x) => x.userId));
    setTrainees(
      availableUsers.filter((x) => !alreadyEnrolledIds.has(x.userId))
    );
  }

  useEffect(() => {
    if (open && !traineesCreated) {
      fetchStudentUsers();
      setSelectedTrainees([]);
    }
  }, [open]);

  return (
    <>
      <Modal
        backdropClassName={"add-trainee-modal-backdrop"}
        modalClassName={"add-trainee-modal"}
        open={open}
        onClose={() => onCloseHandler()}
        preventClose={true}
      >
        {loading && <Loader overlay></Loader>}
        <ModalHeader
          className="blue-icon"
          icon={["fa-light", "head-side-goggles"]}
          title={`Add ${singular}`}
        ></ModalHeader>
        <ModalBody>
          <FormProvider {...methods}>
            <form>
              <TraineesEditor
                traineeData={trainees}
                onAddTrainees={onAddTrainees}
                onCreateTrainee={onCreateTrainee}
                userLabels={userLabels}
                classroomProduct={classroomProduct}
                classroomId={classroomId}
                openRosterModal={openRosterModal}
                bulkUploadEnabled={!isProfessionalLearning}
                creationEnabled={!isProfessionalLearning}
              ></TraineesEditor>
            </form>
          </FormProvider>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => onCloseHandler()}>Cancel</Button>
          <Button
            primary
            onClick={onAddTraineeHandler}
            disabled={selectedTrainees.length === 0}
          >
            Add {selectedTrainees.length > 1 ? plural : singular}
          </Button>
        </ModalFooter>
      </Modal>
      <ConfirmCancelModal
        open={openConfirmCancel}
        onConfirmClose={onConfirmCloseHandler}
        onBackToModal={onBackToModalHandler}
        userLabel={plural}
      />
    </>
  );
}
